import { useLanguage } from "../../hooks/useLanguage"
import { LogoSymbol } from "../Logo"
import { ArrowUp } from "../icons"

// Os números, preços e horários dos mockups são ilustrativos e ficam só aqui.
const barHeights = [34, 48, 40, 60, 54, 68, 58, 78, 66, 86, 80, 98, 92, 114]
const barColor = (i: number) => (i >= 12 ? "#6E4BFF" : i >= 10 ? "#A893FF" : "#D4C9FF")
const slots = ["09:00", "10:30", "14:00", "16:30"]

const tileBase = "gms-tile relative min-w-0 overflow-hidden rounded-tile p-8"

function WebTile() {
  const { t, language } = useLanguage()
  const w = t.bento.web
  return (
    <article className={`${tileBase} min-h-[480px] flex-[1.8_1_600px] bg-gms-mist`}>
      <div className="gms-tile-label text-gms-muted">{w.eyebrow}</div>
      <h2 className="gms-tile-title max-w-[470px]">{w.title}</h2>

      <div
        aria-hidden="true"
        className="mt-7 flex h-[300px] overflow-hidden rounded-card border border-gms-line bg-white shadow-[0_30px_60px_-30px_rgba(11,13,18,0.28)]"
      >
        <div className="gms-dashside flex w-[60px] flex-none flex-col items-center gap-3 border-r border-gms-line-soft py-[18px]">
          <LogoSymbol size={28} />
          <span className="mt-[6px] h-7 w-7 rounded-lg bg-gms-accent-soft" />
          <span className="h-7 w-7 rounded-lg bg-gms-line-soft" />
          <span className="h-7 w-7 rounded-lg bg-gms-line-soft" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-4 px-[22px] py-5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[16px] font-semibold">{w.overview}</span>
            <span className="inline-flex gap-[6px]">
              <span className="rounded-full bg-gms-ink px-[10px] py-1 font-mono text-[12px] text-white">{w.week}</span>
              <span className="rounded-full border border-gms-line px-[10px] py-1 font-mono text-[12px] text-gms-muted">{w.month}</span>
            </span>
          </div>
          <div className="flex gap-3">
            {w.kpis.map((k) => (
              <div key={k.label} className="gms-kpi min-w-0 flex-1 rounded-xl border border-gms-line-soft px-[14px] py-3">
                <div className="font-mono text-[11px] text-gms-subtle">{k.label}</div>
                {k.delta ? (
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-[22px] font-semibold tracking-[-0.02em]">{k.value}</span>
                    <span className="font-mono text-[11px] text-gms-success">{k.delta}</span>
                  </div>
                ) : (
                  <div className="mt-1 text-[22px] font-semibold tracking-[-0.02em]">{k.value}</div>
                )}
              </div>
            ))}
          </div>
          <div className="flex min-h-0 flex-1 items-end gap-2 border-b border-gms-line-soft">
            {barHeights.map((h, i) => (
              <span key={i} className="flex-1 rounded-t-[5px]" style={{ height: h, background: barColor(i) }} />
            ))}
          </div>
        </div>
      </div>

      <div
        aria-hidden="true"
        className="absolute bottom-8 right-8 inline-flex items-center gap-[10px] rounded-xl bg-gms-ink px-4 py-[11px] font-mono text-[13px] text-white shadow-[0_18px_36px_-14px_rgba(11,13,18,0.55)]"
      >
        <span className="text-[#7EE2B8]">POST</span>
        <span>{language === "en" ? "/api/orders" : "/api/pedidos"}</span>
        <span className="text-gms-dark-label">201 · 38 ms</span>
      </div>
    </article>
  )
}

function AppTile() {
  const { t } = useLanguage()
  const a = t.bento.app
  return (
    <article className={`${tileBase} min-h-[480px] flex-[1_1_340px] bg-gms-accent text-white`}>
      <div className="gms-tile-label">{a.eyebrow}</div>
      <h2 className="gms-tile-title max-w-[300px]">{a.title}</h2>

      <div
        aria-hidden="true"
        className="gms-phone absolute left-1/2 top-[186px] h-[460px] w-[244px] rounded-[40px] border-8 border-gms-ink bg-white px-[18px] py-[26px] text-gms-ink shadow-[0_30px_60px_-20px_rgba(11,13,18,0.45)]"
      >
        <div className="text-[18px] font-semibold tracking-[-0.02em]">{a.screenTitle}</div>
        <div className="mt-1 font-mono text-[11.5px] text-gms-subtle">{a.screenSubtitle}</div>
        <div className="mt-4 flex gap-[6px]">
          {a.days.map(({ d, n }, i) => {
            const on = i === 1
            return (
              <span
                key={n}
                className={`flex flex-1 flex-col items-center gap-[2px] rounded-xl border py-2 text-[11px] ${
                  on ? "border-gms-ink bg-gms-ink text-gms-dark-text" : "border-gms-line text-gms-subtle"
                }`}
              >
                {d}
                <b className={`text-[16px] ${on ? "text-white" : "text-gms-ink"}`}>{n}</b>
              </span>
            )
          })}
        </div>
        <div className="mt-[14px] flex flex-wrap gap-[6px]">
          {slots.map((s) => {
            const on = s === "14:00"
            return (
              <span
                key={s}
                className={`flex-[1_1_40%] rounded-xl py-[11px] text-center font-mono text-[13px] ${
                  on ? "border-[1.5px] border-gms-accent bg-gms-accent-soft font-medium text-gms-accent" : "border border-gms-line"
                }`}
              >
                {s}
              </span>
            )
          })}
        </div>
        <div className="mt-4 rounded-[14px] bg-gms-accent py-[13px] text-center text-[14px] font-semibold text-white">{a.confirm}</div>
      </div>
    </article>
  )
}

function AiTile() {
  const { t } = useLanguage()
  const a = t.bento.ai
  return (
    <article className={`${tileBase} flex min-h-[440px] flex-[1_1_360px] flex-col bg-gms-ink text-white`}>
      <div className="gms-tile-label text-gms-dark-label">{a.eyebrow}</div>
      <h2 className="gms-tile-title max-w-[360px]">{a.title}</h2>

      <div aria-hidden="true" className="mt-7 flex flex-col gap-3">
        <div className="max-w-[82%] self-end rounded-[18px_18px_4px_18px] bg-gms-accent px-4 py-3 text-[15px] leading-[1.4] text-white">
          {a.question}
        </div>
        <div className="max-w-[90%] self-start rounded-[18px_18px_18px_4px] bg-gms-dark-2 px-4 py-[14px] text-[15px] leading-[1.45] text-gms-dark-fg">
          {a.answer}
        </div>
        <div className="flex flex-wrap gap-[6px]">
          {a.sources.map((s) => (
            <span key={s} className="rounded-full border border-gms-dark-line-2 px-[10px] py-[5px] font-mono text-[11.5px] text-gms-dark-label">
              {s}
            </span>
          ))}
        </div>
      </div>

      <div aria-hidden="true" className="mt-auto pt-5">
        <div className="flex items-center gap-[10px] rounded-[14px] border border-gms-dark-edge bg-gms-dark-1 py-2 pl-4 pr-2">
          <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[14px] text-gms-dark-muted">{a.placeholder}</span>
          <span className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-[9px] bg-gms-accent text-white">
            <ArrowUp size={16} />
          </span>
        </div>
      </div>
    </article>
  )
}

function Web3Tile() {
  const { t } = useLanguage()
  const w = t.bento.web3
  const [first, second, third] = w.items
  return (
    <article className={`${tileBase} flex min-h-[440px] flex-[1_1_360px] flex-col bg-gms-mist`}>
      <div className="gms-tile-label text-gms-muted">{w.eyebrow}</div>
      <h2 className="gms-tile-title max-w-[360px]">{w.title}</h2>

      <div aria-hidden="true" className="mt-5 rounded-card bg-gms-ink p-[14px] text-white shadow-[0_24px_48px_-28px_rgba(11,13,18,0.5)]">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] font-semibold">{w.collection}</span>
          <span className="font-mono text-[11px] text-gms-dark-muted">{w.standard}</span>
        </div>
        <div className="mt-[10px] grid grid-cols-3 gap-2">
          <div className="rounded-xl border-[1.5px] border-gms-accent-on-dark bg-[rgba(110,75,255,0.18)] px-[5px] pb-2 pt-[5px]">
            <div className="relative aspect-square overflow-hidden rounded-lg bg-gms-accent">
              <span className="absolute -bottom-[14%] -left-[14%] h-[72%] w-[72%] rounded-full bg-white" />
              <span className="absolute right-[14%] top-[14%] h-[22%] w-[22%] rounded-[3px] bg-gms-ink" />
            </div>
            <div className="mt-[7px] px-[3px] font-mono text-[11px] text-white">{first.id}</div>
            <div className="px-[3px] text-[12px] font-semibold text-gms-accent-on-dark">{first.price}</div>
          </div>
          <div className="rounded-xl border border-gms-dark-line bg-gms-dark-1 px-[5px] pb-2 pt-[5px]">
            <div className="relative aspect-square overflow-hidden rounded-lg bg-gms-dark-fg">
              <span className="absolute -right-[30%] -top-[30%] h-full w-full rounded-full bg-gms-accent" />
              <span className="absolute bottom-[18%] left-[16%] h-[12%] w-[46%] rounded-[3px] bg-gms-ink" />
            </div>
            <div className="mt-[7px] px-[3px] font-mono text-[11px] text-gms-dark-text">{second.id}</div>
            <div className="px-[3px] text-[12px] font-semibold text-gms-dark-fg">{second.price}</div>
          </div>
          <div className="rounded-xl border border-gms-dark-line bg-gms-dark-1 px-[5px] pb-2 pt-[5px]">
            <div className="relative aspect-square overflow-hidden rounded-lg bg-gms-dark-tile">
              <span className="absolute left-[18%] top-[18%] box-border h-[64%] w-[64%] rounded-full border-[3px] border-gms-accent-on-dark" />
              <span className="absolute left-[34%] top-[34%] box-border h-[32%] w-[32%] rounded-full border-[3px] border-gms-dark-fg" />
              <span className="absolute right-[10%] top-[10%] h-[12%] w-[12%] rounded-[2px] bg-gms-accent" />
            </div>
            <div className="mt-[7px] px-[3px] font-mono text-[11px] text-gms-dark-text">{third.id}</div>
            <div className="px-[3px] text-[12px] font-semibold text-gms-dark-fg">{third.price}</div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] font-semibold">{w.selected}</span>
            <span className="mt-[2px] block font-mono text-[11px] text-gms-dark-muted">{w.royalty}</span>
          </span>
          <span className="inline-flex h-[34px] flex-none items-center rounded-full bg-gms-accent px-4 text-[13px] font-semibold text-white">{w.buy}</span>
        </div>
        <div className="mt-3 flex justify-between gap-[10px] border-t border-gms-dark-tile pt-[10px] font-mono text-[11.5px] text-gms-dark-text">
          <span>
            <span className="text-gms-mint">{w.sold}</span> · {w.soldDetail}
          </span>
          <span className="text-gms-dark-muted">{w.ago}</span>
        </div>
      </div>

      <div className="mt-auto flex flex-col gap-3 pt-4">
        <div className="flex flex-wrap items-center gap-[6px]">
          <span className="mr-1 font-mono text-[12px] text-gms-muted">{w.also}</span>
          {w.alsoItems.map((item) => (
            <span key={item} className="rounded-full border border-gms-line-2 px-[11px] py-[6px] font-mono text-[12px] text-gms-text-2">
              {item}
            </span>
          ))}
        </div>
      </div>
    </article>
  )
}

// Barras do cronograma: início e largura em % das 4 semanas
const ganttBars = [
  { left: "0%", width: "25%", color: "bg-gms-accent" },
  { left: "25%", width: "50%", color: "bg-gms-accent" },
  { left: "75%", width: "25%", color: "bg-gms-ink" },
]

function MvpTile() {
  const { t } = useLanguage()
  const m = t.bento.mvp
  return (
    <article className={`${tileBase} flex min-h-[440px] flex-[1.25_1_420px] flex-col bg-gms-accent-soft`}>
      <div className="gms-tile-label text-gms-text-2">{m.eyebrow}</div>
      <h2 className="gms-tile-title max-w-[380px]">{m.title}</h2>

      <div aria-hidden="true" className="relative mt-7">
        <div className="mb-[10px] flex gap-3">
          <span className="w-24 flex-none" />
          <span className="flex min-w-0 flex-1 font-mono text-[11px] text-gms-muted">
            {m.weeks.map((wk) => (
              <span key={wk} className="flex-1">{wk}</span>
            ))}
          </span>
        </div>
        <div className="flex flex-col gap-[10px]">
          {m.rows.map((row, i) => (
            <div key={row.name} className="flex items-center gap-3">
              <span className="w-24 flex-none text-[15px] font-semibold">{row.name}</span>
              <span className="flex h-[34px] min-w-0 flex-1 rounded-[10px] bg-[rgba(11,13,18,0.06)]">
                <span
                  className={`gms-gbar flex items-center overflow-hidden text-ellipsis whitespace-nowrap rounded-[10px] pl-[10px] font-mono text-[11.5px] text-white ${ganttBars[i].color}`}
                  style={{ marginLeft: ganttBars[i].left, width: ganttBars[i].width }}
                >
                  {row.bar}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>

      <p className="mt-auto pt-5 text-[16px] leading-[1.5] text-gms-ink-2">{m.note}</p>
    </article>
  )
}

export default function Bento() {
  const { t } = useLanguage()
  return (
    <section
      id="servicos"
      aria-label={t.bento.label}
      className="mx-auto mt-[clamp(56px,6vw,88px)] flex max-w-gms flex-col gap-4 px-gutter"
    >
      <div className="flex flex-wrap gap-4">
        <WebTile />
        <AppTile />
      </div>
      <div className="flex flex-wrap gap-4">
        <AiTile />
        <Web3Tile />
        <MvpTile />
      </div>
    </section>
  )
}
